import React, { useState, useRef } from "react";

import { EditorState } from 'draft-js';
import { connect } from 'react-redux';

import * as SC from './SpellcheckFull.styled';
import { AdvicePanel } from '../../Components/Advice/AdvicePanel';
import { CorrectionModeButtons } from '../../Components/CorrectionModeButtons/CorrectionModeButtons';
import { Handshake } from '../../Components/Handshake/Handshake';
import { OneClickHandshake } from '../../Components/Handshake/OneClickHandshake';
import { LoadingDotsIcon } from '../../Components/Icons';
import { InlineAdviceWrapper } from '../../Components/InlineAdvice/InlineAdviceWrapper';
import { MetaMenu } from '../../Components/MetaMenu/MetaMenu';
import { ApplyTextChangesModal, OneClickConsentModal } from '../../Components/Modal';
import OneClickPad from '../../Components/OneClickPad/OneClickPad';
import { Pad } from '../../Components/Pad/Pad';
import { Profiler } from '../../Components/Profiler/Profiler';
import { Rim } from '../../Components/Rim/Rim';
import { StatusMessages } from '../../Components/StatusMessages/StatusMessages';
import useOneClickStore, { OneClickStore, selectIsSegmentsLoading } from '../../Hooks/useOneClickStore';
import { selectEditorState, copyOneClickTextAction } from '../../Store/PadState';
import { FeatureFlagProps, selectFeatureFlags } from '../../Store/SettingsState';
import { Colors } from '../../Styles';
import { AdviceErrorWrapper } from '../../Util/AdviceErrorWrapper';
import { GTMDataLayerPush } from '../../Util/GoogleTagManager';
import { PadErrorWrapper } from '../../Util/PadErrorWrapper';

interface DispatchProps {
    editorState: EditorState;
    featureFlags: FeatureFlagProps[];
}

interface DispatchActions {
    onCopyOneClickText: typeof copyOneClickTextAction;
}

type SpellcheckFullProps = DispatchProps & DispatchActions;


export const SpellcheckComponent: React.FC<SpellcheckFullProps> = ({
    editorState,
    featureFlags,
    onCopyOneClickText,
}) => {
    const isSegmentsLoading = useOneClickStore(selectIsSegmentsLoading);
    const isOneClickAgreed = useOneClickStore((store: OneClickStore) => store.isOneClickAgreed);
    const isOneClickModeActive = useOneClickStore((store: OneClickStore) => store.isOneClickModeActive);
    const setIsOneClickModeActive = useOneClickStore((store: OneClickStore) => store.setIsOneClickModeActive);
    const [oneClickModal, setOneClickModal] = useState(false);
    const [applyTextChangesModal, setApplyTextChangesModal] = useState(false);

    const deskRef = useRef(null);

    const isOneClickFlagActive = featureFlags["click_correction"];

    const changeToOneClickView = () => setIsOneClickModeActive(true);

    const keepOneClickContent = () => {
        onCopyOneClickText({
            editorState,
        });
    };

    const handleOneClickButton = () => {
        // Skip show popup or change to ONE-CLICK mode if user already on ONE-CLICK editor.
        if (isOneClickModeActive) {
            return;
        }

        GTMDataLayerPush({
            event: "spellcheck_select_option",
            option: "mentor_express"
        });

        if (isOneClickAgreed) {
            // storeOneClickText();
            changeToOneClickView();
        } else {
            setOneClickModal(true);
        }
    };

    const handleClassicButton = () => {
        // Skip show popup or change to CLASSIC mode if user already on CLASSIC editor.
        if (!isOneClickModeActive) {
            return;
        }

        // TODO: clean up if logic is obsolete
        // If empty editor - change editor mode,
        // if exist some text we ask restore old text or move changed text to classic mode.
        // const oneClickPadElement = document.querySelector(".oneClickPad");
        // const textContent = oneClickContent.trim();
        // (textContent && textContent.length > 0)
        //     ? setApplyTextChangesModal(true)
        //     : setIsOneClickModeActive(false);

        GTMDataLayerPush({
            event: "spellcheck_select_option",
            option: "mentor_standard"
        });
        setIsOneClickModeActive(false);
    };

    return (
        <>
            <SC.Desk>
                <SC.Editor>
                    <StatusMessages />
                    <SC.DeskContent ref={deskRef} viewMode={isOneClickModeActive}>
                        {isOneClickModeActive ? (
                            <>
                                <OneClickPad />
                            </>
                        ) : (
                            <>
                                <InlineAdviceWrapper deskRef={deskRef}/>
                                <PadErrorWrapper>
                                    <Pad/>
                                </PadErrorWrapper>
                            </>
                        )}
                    </SC.DeskContent>

                    <footer>
                        <Rim />
                    </footer>
                </SC.Editor>

                <SC.RightColumnWrapper columnColor={isOneClickModeActive ? `${Colors.cottonBoll}` : `${Colors.mystic}`}>
                    <ApplyTextChangesModal
                        closeApplyTextChangesModal={() => setApplyTextChangesModal(false)}
                        applyTextChangesModal={applyTextChangesModal}
                        keepOneClickContent={keepOneClickContent}
                    />
                    <SC.AdviceWrapper>
                        {isOneClickModeActive && isSegmentsLoading && <SC.StyledLoadingIcon>
                            <LoadingDotsIcon fill={Colors.mineShaft}/>
                        </SC.StyledLoadingIcon>}

                        {/* Feature flag can be removed, as soon as the 1-click-feature is implemented permanently */}
                        {isOneClickFlagActive && (
                            <CorrectionModeButtons
                                handleClassicButton={handleClassicButton}
                                handleOneClickButton={handleOneClickButton}
                            />
                        )}
                        {!isOneClickModeActive && (
                            <div>
                                <AdviceErrorWrapper>
                                    <AdvicePanel/>
                                    <Handshake/>
                                </AdviceErrorWrapper>
                                <Profiler/>
                            </div>
                        )}
                        {isOneClickModeActive && <OneClickHandshake />}
                        <MetaMenu />
                    </SC.AdviceWrapper>
                </SC.RightColumnWrapper>
            </SC.Desk>
            <OneClickConsentModal
                closeOneClickModal={() => setOneClickModal(false)}
                oneClickModal={oneClickModal}
            />
        </>
    );
};

const mapStateToProps = (store: EditorState): DispatchProps => ({
    editorState: selectEditorState(store),
    featureFlags: selectFeatureFlags(store),
});

const dispatchMap: DispatchActions = {
    onCopyOneClickText: copyOneClickTextAction,
};

export const SpellcheckFull = connect(mapStateToProps, dispatchMap)(SpellcheckComponent);
